import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	type ChartOptions,
	Legend,
	LinearScale,
	Title,
	Tooltip,
} from 'chart.js'
import type React from 'react'
import { Bar } from 'react-chartjs-2'
import { cn } from '../../lib/cva'
import { Text } from '../Text'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export interface BarChartDataItem {
	label: string
	value: number
	color?: string
	percentage?: number
}

export interface BarChartProps {
	data: BarChartDataItem[]
	title?: string
	orientation?: 'vertical' | 'horizontal'
	showLegend?: boolean
	showTooltips?: boolean
	showPercentages?: boolean
	showValues?: boolean
	showGrid?: boolean
	height?: number
	width?: number
	className?: string
	colors?: string[]
	maxBarThickness?: number
	spacing?: number
}

const defaultColors = [
	'#3b82f6',
	'#ef4444',
	'#f59e0b',
	'#10b981',
	'#8b5cf6',
	'#f97316',
	'#06b6d4',
	'#84cc16',
	'#ec4899',
	'#6b7280',
]

export const BarChart: React.FC<BarChartProps> = ({
	data,
	title,
	orientation = 'horizontal',
	showLegend = false,
	showTooltips = true,
	showPercentages = true,
	showValues = true,
	height,
	width,
	className,
	colors = defaultColors,
	maxBarThickness = 60,
	spacing = 0.5,
}) => {
	const totalValue = data.reduce((sum, item) => sum + item.value, 0)

	const dataWithPercentages = data.map((item, index) => ({
		...item,
		percentage:
			item.percentage !== undefined
				? item.percentage
				: totalValue > 0
					? Math.round((item.value / totalValue) * 100)
					: 0,
		color: item.color || colors[index % colors.length],
	}))

	const chartData = {
		labels: dataWithPercentages.map((item) => item.label),
		datasets: [
			{
				data: dataWithPercentages.map((item) => item.value),
				backgroundColor: dataWithPercentages.map((item) => item.color),
				borderColor: dataWithPercentages.map((item) => item.color),
				borderWidth: 1,
				maxBarThickness,
				categoryPercentage: spacing,
				barPercentage: 0.9,
				borderRadius: orientation === 'horizontal' ? { topRight: 8, bottomRight: 8 } : { topLeft: 8, topRight: 8 },
				borderSkipped: false,
			},
		],
	}

	const options: ChartOptions<'bar'> = {
		indexAxis: orientation === 'horizontal' ? 'y' : 'x',
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: showLegend,
				position: 'top',
			},
			tooltip: {
				enabled: showTooltips,
				callbacks: {
					label: (context) => {
						const item = dataWithPercentages[context.dataIndex]
						const parts = []
						if (showValues) parts.push(`${item.value}`)
						if (showPercentages) parts.push(`(${item.percentage}%)`)
						return parts.length > 0 ? parts.join(' ') : item.value.toString()
					},
				},
			},
		},
		scales: {
			x: {
				display: orientation === 'vertical',
				grid: {
					display: false,
				},
				ticks: {
					display: false,
					maxRotation: orientation === 'vertical' ? 45 : 0,
				},
			},
			y: {
				display: orientation === 'horizontal',
				grid: {
					display: false,
				},
				ticks: {
					display: orientation === 'horizontal',
					align: 'start',
					crossAlign: 'far',
				},
				beginAtZero: true,
			},
		},
		animation: {
			duration: 750,
			easing: 'easeInOutQuart',
			onComplete: (context) => {
				const chart = context.chart
				const ctx = chart.ctx

				if (showValues || showPercentages) {
					ctx.save()
					ctx.textAlign = 'right'
					ctx.textBaseline = 'middle'
					ctx.fillStyle = '#1E293B'

					chart.data.datasets.forEach((_dataset, datasetIndex) => {
						const meta = chart.getDatasetMeta(datasetIndex)
						meta.data.forEach((bar, index) => {
							const item = dataWithPercentages[index]
							const parts = []
							if (showValues) parts.push(`${item.value}`)
							if (showPercentages) parts.push(`(${item.percentage}%)`)
							const label = parts.join(' ')

							if (orientation === 'horizontal') {
								const x = bar.x - 10
								const y = bar.y
								ctx.fillText(label, x, y)
							} else {
								const x = bar.x
								const y = bar.y + 15
								ctx.save()
								ctx.textAlign = 'center'
								ctx.fillText(label, x, y)
								ctx.restore()
							}
						})
					})
					ctx.restore()
				}
			},
		},
	}

	const dynamicHeight = height || (orientation === 'horizontal' ? Math.max(200, data.length * 100) : 400)

	const containerStyle = {
		height: `${dynamicHeight}px`,
		...(width && { width: `${width}px` }),
	}

	return (
		<div className={cn('w-full', className)}>
			{title && (
				<Text variant='h5' weight='semibold' className='mb-4'>
					{title}
				</Text>
			)}

			<div style={containerStyle}>
				<Bar data={chartData} options={options} />
			</div>
		</div>
	)
}

export default BarChart
